import React, { useEffect, useMemo, useState } from 'react';
import {
  Users, UserPlus, Mail, Phone, Trash2, Edit2, Loader2, AlertCircle,
  CheckCircle2, Eye, EyeOff, ShieldCheck, ShoppingCart, ClipboardCheck,
} from 'lucide-react';
import {
  operatoriService,
  Operatore,
  OperatoreRuolo,
  RUOLO_DESCRIPTIONS,
} from '../../services/operatoriService';

const RUOLO_ICON: Record<OperatoreRuolo, React.ReactNode> = {
  'vedi-sito': <ShieldCheck className="w-4 h-4" />,
  'crea-carrello': <ShoppingCart className="w-4 h-4" />,
  'crea-ordine': <ClipboardCheck className="w-4 h-4" />,
};

const RUOLO_COLOR: Record<OperatoreRuolo, string> = {
  'vedi-sito': 'bg-slate-100 text-slate-700',
  'crea-carrello': 'bg-amber-100 text-amber-800',
  'crea-ordine': 'bg-emerald-100 text-emerald-800',
};

export default function OperatoriPage() {
  const [items, setItems] = useState<Operatore[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Operatore | 'new' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Operatore | null>(null);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await operatoriService.list());
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Errore caricamento operatori');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const handleSaved = () => {
    setEditing(null);
    reload();
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await operatoriService.remove(confirmDelete.operatoreId);
      setConfirmDelete(null);
      reload();
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Errore eliminazione operatore');
    }
  };

  return (
    <div data-testid="operatori-page" className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Gestione operatori</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Crea account secondari per i tuoi collaboratori, con permessi su misura.
          </p>
        </div>
        <button
          onClick={() => setEditing('new')}
          disabled={items.length >= 5}
          data-testid="add-operatore"
          className="h-10 px-4 rounded-lg bg-[hsl(210,64%,29%)] text-white text-sm font-semibold hover:bg-[hsl(210,64%,32%)] inline-flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <UserPlus className="w-4 h-4" /> Nuovo operatore
        </button>
      </div>

      {items.length >= 5 && !loading && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 flex-none" /> Hai raggiunto il limite massimo di 5 operatori per account.
        </div>
      )}

      {/* Spiegazione ruoli */}
      <div className="rounded-xl border bg-[hsl(210,40%,98%)] p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        {(Object.keys(RUOLO_DESCRIPTIONS) as OperatoreRuolo[]).map(r => (
          <div key={r} className="flex items-start gap-2.5">
            <div className={`w-8 h-8 rounded-lg ${RUOLO_COLOR[r]} flex items-center justify-center flex-none`}>
              {RUOLO_ICON[r]}
            </div>
            <div>
              <div className="text-sm font-semibold">{RUOLO_DESCRIPTIONS[r].label}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{RUOLO_DESCRIPTIONS[r].desc}</div>
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-center gap-2" data-testid="op-error">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* Lista */}
      <div className="rounded-xl border bg-white">
        {loading ? (
          <div className="p-10 flex items-center justify-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 px-6" data-testid="op-empty">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <h3 className="text-base font-semibold">Nessun operatore</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Crea il primo account secondario per condividere l'accesso con il tuo team.
            </p>
          </div>
        ) : (
          <ul className="divide-y" data-testid="op-list">
            {items.map(op => (
              <li
                key={op.operatoreId}
                data-testid={`op-item-${op.operatoreId}`}
                className={`px-4 py-3 flex items-center gap-3 ${op.attivo ? '' : 'opacity-60'}`}
              >
                <div className={`w-10 h-10 rounded-full bg-[hsl(210,40%,96%)] flex items-center justify-center text-[hsl(210,64%,29%)] font-semibold flex-none`}>
                  {(op.nome?.[0] || '') + (op.cognome?.[0] || '')}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{op.nome} {op.cognome}</div>
                  <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                    <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {op.email}</span>
                    {op.telefono && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {op.telefono}</span>}
                  </div>
                </div>
                <span className={`text-[11px] font-semibold px-2 py-1 rounded-full ${RUOLO_COLOR[op.ruolo]} inline-flex items-center gap-1`}>
                  {RUOLO_ICON[op.ruolo]} {op.ruoloLabel}
                </span>
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${op.attivo ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                  {op.attivo ? 'Attivo' : 'Sospeso'}
                </span>
                <button
                  onClick={() => setEditing(op)}
                  data-testid={`op-edit-${op.operatoreId}`}
                  className="p-2 text-muted-foreground hover:text-[hsl(210,64%,29%)] hover:bg-[hsl(210,40%,96%)] rounded-lg"
                  aria-label="Modifica"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setConfirmDelete(op)}
                  data-testid={`op-delete-${op.operatoreId}`}
                  className="p-2 text-muted-foreground hover:text-rose-700 hover:bg-rose-50 rounded-lg"
                  aria-label="Elimina"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <OperatoreFormDialog
          operatore={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={handleSaved}
        />
      )}
      {confirmDelete && (
        <ConfirmDeleteDialog
          operatore={confirmDelete}
          onClose={() => setConfirmDelete(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}

function OperatoreFormDialog({ operatore, onClose, onSaved }: {
  operatore: Operatore | null; onClose: () => void; onSaved: () => void;
}) {
  const isEdit = !!operatore;
  const [form, setForm] = useState({
    email: operatore?.email ?? '',
    nome: operatore?.nome ?? '',
    cognome: operatore?.cognome ?? '',
    telefono: operatore?.telefono ?? '',
    ruolo: (operatore?.ruolo ?? 'vedi-sito') as OperatoreRuolo,
    attivo: operatore?.attivo ?? true,
    password: '',
    changePassword: !isEdit,
  });
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const canSubmit = useMemo(() => {
    if (!form.email.includes('@')) return false;
    if (!form.nome.trim() || !form.cognome.trim()) return false;
    if (!isEdit && form.password.length < 8) return false;
    if (form.changePassword && form.password.length < 8) return false;
    return true;
  }, [form, isEdit]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setSuccess(false);
    try {
      if (isEdit && operatore) {
        const payload: any = {
          nome: form.nome,
          cognome: form.cognome,
          telefono: form.telefono,
          ruolo: form.ruolo,
          attivo: form.attivo,
        };
        if (form.changePassword && form.password) payload.password = form.password;
        await operatoriService.update(operatore.operatoreId, payload);
      } else {
        await operatoriService.create({
          email: form.email,
          nome: form.nome,
          cognome: form.cognome,
          telefono: form.telefono || undefined,
          ruolo: form.ruolo,
          password: form.password,
        });
      }
      setSuccess(true);
      setTimeout(onSaved, 400);
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? 'Errore salvataggio');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="op-dialog" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl border shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="border-b px-5 py-3 flex items-center justify-between">
          <h3 className="text-lg font-semibold">{isEdit ? 'Modifica operatore' : 'Nuovo operatore'}</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Chiudi">✕</button>
        </div>

        <form onSubmit={submit} className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="Nome" value={form.nome} onChange={(v) => setForm({ ...form, nome: v })} required testId="op-input-nome" />
            <Input label="Cognome" value={form.cognome} onChange={(v) => setForm({ ...form, cognome: v })} required testId="op-input-cognome" />
            <Input label="Email (usata per il login)" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required disabled={isEdit} testId="op-input-email" />
            <Input label="Telefono (opzionale)" value={form.telefono || ''} onChange={(v) => setForm({ ...form, telefono: v })} testId="op-input-telefono" />
          </div>

          {/* Ruoli */}
          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Ruolo</label>
            <div className="space-y-2" data-testid="op-ruolo-group">
              {(Object.keys(RUOLO_DESCRIPTIONS) as OperatoreRuolo[]).map(r => (
                <label
                  key={r}
                  data-testid={`op-ruolo-${r}`}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    form.ruolo === r ? 'border-[hsl(210,64%,29%)] bg-[hsl(210,40%,98%)]' : 'hover:bg-[hsl(0,0%,98%)]'
                  }`}
                >
                  <input
                    type="radio"
                    name="ruolo"
                    checked={form.ruolo === r}
                    onChange={() => setForm({ ...form, ruolo: r })}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm flex items-center gap-2">
                      {RUOLO_ICON[r]} {RUOLO_DESCRIPTIONS[r].label}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{RUOLO_DESCRIPTIONS[r].desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Password */}
          <div>
            {isEdit && (
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer mb-2">
                <input
                  type="checkbox"
                  checked={form.changePassword}
                  onChange={(e) => setForm({ ...form, changePassword: e.target.checked, password: '' })}
                  data-testid="op-change-pw"
                />
                <span>Cambia password</span>
              </label>
            )}
            {(!isEdit || form.changePassword) && (
              <div>
                <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                  {isEdit ? 'Nuova password' : 'Password iniziale'}
                </label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    autoComplete="new-password"
                    data-testid="op-input-password"
                    className="w-full h-10 pl-3 pr-10 rounded-lg border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(210,64%,29%)]/30"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(s => !s)}
                    className="absolute right-2 top-2 p-1 text-muted-foreground"
                    tabIndex={-1}
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">Minimo 8 caratteri.</p>
              </div>
            )}
          </div>

          {isEdit && (
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.attivo}
                onChange={(e) => setForm({ ...form, attivo: e.target.checked })}
                data-testid="op-attivo"
              />
              <span>Operatore attivo</span>
            </label>
          )}

          {error && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          )}
          {success && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Salvato.
            </div>
          )}

          <div className="border-t pt-3 flex items-center justify-end gap-2">
            <button type="button" onClick={onClose} className="h-9 px-4 rounded-lg border text-sm hover:bg-[hsl(0,0%,97%)]">
              Annulla
            </button>
            <button
              type="submit"
              disabled={!canSubmit || submitting}
              data-testid="op-submit"
              className="h-9 px-5 rounded-lg bg-[hsl(210,64%,29%)] text-white text-sm font-semibold hover:bg-[hsl(210,64%,32%)] disabled:opacity-40"
            >
              {submitting ? 'Salvataggio…' : (isEdit ? 'Aggiorna' : 'Crea operatore')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConfirmDeleteDialog({ operatore, onClose, onConfirm }: {
  operatore: Operatore; onClose: () => void; onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="op-delete-dialog" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl border shadow-xl w-full max-w-md p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center flex-none">
            <Trash2 className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Eliminare l'operatore?</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Stai per rimuovere <strong>{operatore.nome} {operatore.cognome}</strong> ({operatore.email}). L'accesso verrà revocato immediatamente.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <button onClick={onClose} className="h-9 px-4 rounded-lg border text-sm hover:bg-[hsl(0,0%,97%)]">Annulla</button>
          <button
            onClick={onConfirm}
            data-testid="op-confirm-delete"
            className="h-9 px-5 rounded-lg bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700"
          >
            Elimina
          </button>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, required, disabled, type, testId }: {
  label: string; value: string; onChange: (v: string) => void;
  required?: boolean; disabled?: boolean; type?: string; testId?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
        {label}{required && <span className="text-rose-600">*</span>}
      </label>
      <input
        type={type || 'text'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        required={required}
        data-testid={testId}
        className="w-full h-10 px-3 rounded-lg border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(210,64%,29%)]/30 disabled:bg-[hsl(0,0%,96%)] disabled:cursor-not-allowed"
      />
    </div>
  );
}
